// ── Caption types ──────────────────────────────────────────────────────────────

export interface WordTimestamp {
  word:         string
  startSeconds: number
  endSeconds:   number
}

export interface CaptionSegment {
  startSeconds: number
  endSeconds:   number
  text:         string
  words?:       WordTimestamp[]
}

// ── Karaoke caption styles ─────────────────────────────────────────────────────

export type CaptionStyle = 'white' | 'yellow' | 'green' | 'blue' | 'black'

export const CAPTION_STYLES: CaptionStyle[] = ['white', 'yellow', 'green', 'blue', 'black']

export const STYLE_CONFIG: Record<CaptionStyle, { active: string; inactive: string; bg: string; swatch: string }> = {
  white:  { active: '#FFFFFF',  inactive: 'rgba(255,255,255,0.42)', bg: 'rgba(0,0,0,0.68)',  swatch: '#FFFFFF' },
  yellow: { active: '#FFE600',  inactive: 'rgba(255,255,255,0.42)', bg: 'rgba(0,0,0,0.68)',  swatch: '#FFE600' },
  green:  { active: '#4ADE80',  inactive: 'rgba(255,255,255,0.42)', bg: 'rgba(0,0,0,0.68)',  swatch: '#4ADE80' },
  blue:   { active: '#60A5FA',  inactive: 'rgba(255,255,255,0.42)', bg: 'rgba(0,0,0,0.68)',  swatch: '#60A5FA' },
  black:  { active: '#111111',  inactive: 'rgba(0,0,0,0.45)',       bg: 'rgba(255,255,255,0.90)', swatch: '#1a1a1a' },
}

// ── KaraokeCaptions ────────────────────────────────────────────────────────────

interface KaraokeProps {
  words:        WordTimestamp[]
  currentTime:  number
  style:        CaptionStyle
  wordsPerLine: number
}

export function KaraokeCaptions({ words, currentTime, style, wordsPerLine }: KaraokeProps) {
  const cfg = STYLE_CONFIG[style]

  const activeIdx = words.findIndex(
    w => currentTime >= w.startSeconds && currentTime < w.endSeconds,
  )

  let clampedIdx: number
  if (activeIdx >= 0) {
    clampedIdx = activeIdx
  } else if (currentTime >= (words[words.length - 1]?.endSeconds ?? 0)) {
    clampedIdx = words.length - 1
  } else {
    let lastSpoken = -1
    for (let i = words.length - 1; i >= 0; i--) {
      if (currentTime >= words[i].endSeconds) { lastSpoken = i; break }
    }
    clampedIdx = lastSpoken
  }

  const lineIndex    = clampedIdx >= 0 ? Math.floor(clampedIdx / wordsPerLine) : -1
  const lineStart    = lineIndex >= 0 ? lineIndex * wordsPerLine : 0
  const lineWords    = lineIndex >= 0 ? words.slice(lineStart, lineStart + wordsPerLine) : []
  const activeInLine = clampedIdx >= 0 ? clampedIdx - lineStart : -1

  if (lineWords.length === 0) return null

  return (
    <div
      key={lineIndex}
      className="absolute bottom-3 left-1/2 -translate-x-1/2 max-w-[94%] z-[2] rounded-md px-3 py-2 flex flex-wrap justify-center gap-x-1.5 pointer-events-none animate-karaoke-fade-in karaoke-line-clamp"
      style={{ background: cfg.bg }}
    >
      {lineWords.map((w, i) => {
        const globalIdx = lineStart + i
        const isActive  = i === activeInLine
        const isPast    = clampedIdx > globalIdx
        const wordDur   = Math.max(0.05, w.endSeconds - w.startSeconds)
        const elapsed   = isActive ? Math.max(0, currentTime - w.startSeconds) : 0

        return (
          <span
            key={`${globalIdx}-${isActive ? 'a' : 'i'}`}
            data-word={w.word}
            className={isActive ? 'kw-fill-active' : undefined}
            style={{
              display:    'inline-block',
              position:   'relative',
              fontSize:   '0.95rem',
              fontWeight: 700,
              lineHeight: 1.5,
              whiteSpace: 'nowrap',
              color: isPast ? cfg.active : cfg.inactive,
              ...(isActive && {
                '--kw-color': cfg.active,
                '--kw-dur':   `${wordDur}s`,
                '--kw-delay': `-${elapsed}s`,
              } as React.CSSProperties),
            }}
          >
            {w.word}
          </span>
        )
      })}
    </div>
  )
}

// ── Fallback plain caption ─────────────────────────────────────────────────────

export function PlainCaption({ text, style }: { text: string; style: CaptionStyle }) {
  const cfg = STYLE_CONFIG[style]
  return (
    <div
      className="absolute bottom-3 left-1/2 -translate-x-1/2 max-w-[90%] z-[2] rounded px-3 py-1 text-[0.9rem] leading-snug text-center pointer-events-none whitespace-pre-wrap"
      style={{ background: cfg.bg, color: cfg.active }}
    >
      {text}
    </div>
  )
}
